import {
    Availability,
    CostDTO,
    OfferIdAndSignatureDTO,
    Option,
    type Quantity,
    SolutionCostDTO,
    SolutionQuantitiesDTO,
    SolutionTag,
    Tag,
} from '@luminovo/api-contracts';

export { SolutionStatus } from '@luminovo/api-contracts';

export interface Solution {
    cost: SolutionCostDTO;
    quantities: SolutionQuantitiesDTO;
    unit: Quantity;
    quantity: number;
    offerUnitPrice: Option<CostDTO>;
    availability: Option<Availability>;
    solutionTags: Tag[];
    offerIdAndSignature: OfferIdAndSignatureDTO;
}

// ranked for importance https://www.figma.com/file/ZHVicAWTv9w0RC9cPgqgld/Sourcing-v1?node-id=699%3A111
export const SolutionNotificationTags: readonly SolutionTag[] = [
    SolutionTag.Excess,
    SolutionTag.QuotePrice,
    SolutionTag.ContractPrice,
    SolutionTag.CustomerNegotiatedPrice,
    SolutionTag.PurchasePrice,
    SolutionTag.StandardPrice,
    SolutionTag.ManualOffer,
    SolutionTag.Customer,
    SolutionTag.RfQ,
    SolutionTag.Negotiation,
    SolutionTag.Converted,
    SolutionTag.OneTimeCost,
    SolutionTag.OnOrder,
    SolutionTag.Consigned,
    SolutionTag.NonCancellableNonReturnable,
    SolutionTag.OnlyValidAsStableSelection,
    SolutionTag.ReferencePrice,
    SolutionTag.OfferValidForWindow,
    SolutionTag.OfferCreatedInWindow,
    SolutionTag.OfferMissingValidFromInfo,
    SolutionTag.OfferMissingValidUntilInfo,
    SolutionTag.ExcludedFromSourcing,
    SolutionTag.PartAlternative,
] as const;

export const SolutionWarningTags: readonly SolutionTag[] = [
    SolutionTag.LowStock,
    SolutionTag.OnePoint,
    SolutionTag.Extrapolated,
    SolutionTag.Interpolated,
    SolutionTag.Old,
    SolutionTag.Expiring,
    SolutionTag.LongLeadTime,
    SolutionTag.UnknownLeadTime,
    SolutionTag.ExceedsOfferedQuantity,
    SolutionTag.OfferMissingValidFromWarn,
    SolutionTag.OfferMissingValidUntilWarn,
    SolutionTag.OfferPartiallyValidForWindow,
    SolutionTag.PendingPartOption,
    SolutionTag.PartAlternativeWarning,
] as const;

export const SolutionErrorTags: readonly SolutionTag[] = [
    SolutionTag.RejectedPartOption,
    SolutionTag.PartOptionMissing,
    SolutionTag.Restricted,
    SolutionTag.SupplierNotApproved,
    SolutionTag.UnitMismatch,
    // Deprecated: no longer emitted by the backend (replaced by UnknownLeadTime). Kept for old snapshots.
    SolutionTag.Unavailable,
    SolutionTag.OutdatedManual,
    SolutionTag.Outdated,
    SolutionTag.Expired,
    SolutionTag.ThirdPartyOrigin,
    SolutionTag.GreatlyExceedsOfferedQuantity,
    SolutionTag.NoApprovedParts,
    SolutionTag.InvalidSpecification,
    SolutionTag.SupplierExcluded,
    SolutionTag.PanelMismatch,
    SolutionTag.ExcludedPriceType,
    SolutionTag.OfferInvalidForWindow,
    SolutionTag.BadOfferValidRange,
    SolutionTag.PreviouslyRestricted,
    SolutionTag.NoOffers,
    SolutionTag.Pending,
] as const;

export enum QuoteRequestFieldCategory {
    QUOTE_REQUEST = 'quoteRequest',
    OFFER_INFORMATION = 'offerInformation',
    PART_INFORMATION = 'partInformation',
}

export enum QuoteRequestFieldConfig {
    ALWAYS_REQUIRED = 'alwaysRequired',
    REQUIRED = 'required',
    HIDDEN = 'hidden',
    OPTIONAL = 'optional',
}
